// LandingPage.tsx
import React, { useEffect, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { MenuCardItem, MenuCardList } from '../organisms/MenuCardList';
import { NewsArticle, NewsSection } from '../organisms/NewsSection';
import { PopularArticles } from '../organisms/PopularArticles';
import { BannerWithSearchBar } from '../organisms/BannerWithSearchBar';
import { Notification, SectionIcons, useSettings } from '../../stateManagement/SettingsContext';
import { Layout } from '../templates';
import { SectionGrid } from '../organisms/SectionGrid';
import { log } from '../../utils';
import { appInsights } from '../../appInsights';
import { NotificationBanner } from '../molecules/NotificationBanner';

export const LandingPage: React.FC = () => {
  const { settings, sectionsData, faqs, language, newsArticles } = useSettings(); // Destructure from context
  const [showSectionGrid, setShowSectionGrid] = useState(
    settings?.configs.showSectionGrid || false,
  );

  const navigate = useNavigate();

  const onClickMore = (title: string) => {
    navigate(`/sections/${language}/section/${title}`);
  };

  const moreClickHref = (title: string) => {
    return `/sections/${language}/section/${title}`;
  };

  useEffect(() => {
    if (!sectionsData || sectionsData.length === 0) {
      setShowSectionGrid(false);
    }
    if (sectionsData?.length !== 0) {
      setShowSectionGrid(true);
    }
  }, [sectionsData]);

  const onClickContactUs = () => {
    // eslint-disable-next-line
    log(settings?.configs.showLogs, 'Contact Us button clicked');
    appInsights.trackEvent({
      name: 'landingPage',
      properties: { buttonName: 'contactUsButton', userName: 'anonymous' },
    });
    navigate('/contactUs');
  };

  const onClickMenuCard = (menuCardLink: string) => {
    window.open(menuCardLink, '_self');
  };

  const onClickPopularArticle = (publish_id: string | number) => {
    const faq = faqs?.find(item => item.publish_id === publish_id);
    if (faq) {
      const translation = faq.translations[language];

      if (translation) {
        const formattedTitle = translation.title.toLowerCase().replace(/\s+/g, '-');
        navigate(`/article/${language}/faq/${publish_id}-${formattedTitle}`);
        appInsights.trackEvent({
          name: 'landingPage',
          properties: {
            buttonName: 'popularArticlesTile',
            userName: 'anonymous',
            publishId: publish_id,
            articleTitle: formattedTitle,
            articleId: faq.id,
            sectionId: faq.section_id,
            category: faq?.section_translations?.[language]?.title,
            language,
          },
        });
      } else {
        const englishTranslation = faq.translations.en;
        const formattedTitle = englishTranslation.title.toLowerCase().replace(/\s+/g, '-');
        navigate(`/article/${language}/faq/${publish_id}-${formattedTitle}`);
        appInsights.trackEvent({
          name: 'landingPage',
          properties: {
            buttonName: 'popularArticlesTile',
            userName: 'anonymous',
            publishId: publish_id,
            articleTitle: formattedTitle,
            articleId: faq.id,
            sectionId: faq.section_id,
            category: faq?.section_translations?.[language]?.title,
            language,
          },
        });
      }
    }
  };

  const popularArticleHref = (publish_id: string | number) => {
    const faq = faqs?.find(item => item.publish_id === publish_id);
    if (faq) {
      const translation = faq.translations[language];
      if (translation) {
        const formattedTitle = translation.title.toLowerCase().replace(/\s+/g, '-');
        return `/article/${language}/faq/${publish_id}-${formattedTitle}`;
      }
      const englishTranslation = faq.translations.en;
      const formattedTitle = englishTranslation.title.toLowerCase().replace(/\s+/g, '-');
      return `/article/${language}/faq/${publish_id}-${formattedTitle}`;
    }
    return '#';
  };

  const { t }: { t: (key: string, options?: { returnObjects: boolean }) => any } = useTranslation();
  const menuList = t('data.menuCards', { returnObjects: true }) as MenuCardItem[];
  // const popularArticleList = t('data.popularArticles', { returnObjects: true }) as FaqArticle[];
  const newsArticleList = t('data.newsArticles', { returnObjects: true }) as NewsArticle[];
  const sectionIconsList = t('data.sectionIcons', { returnObjects: true }) as SectionIcons[];
  const categoryHeadings = t('data.categoryHeadings', { returnObjects: true });
  const helperTexts = t('data.helperTexts', { returnObjects: true });
  const notificationsData = t('data.notifications', { returnObjects: true }) as Notification[];
  return (
    <Layout
      pageBgColor={settings?.configs.secondaryBgColor}
      onContactUsClick={onClickContactUs}
      settings={settings}
    >
      <Helmet>
        <title>Help Center</title>
      </Helmet>
      <Box bgColor={settings?.configs.secondaryBgColor}>
        {/* Banner with Search Bar */}
        {settings?.configs.showBanner && (
          <BannerWithSearchBar
            isFullScreen={settings?.configs.isBannerFullScreen}
            px={{
              base: '0vw',
              md: '0vw',
              lg: settings?.configs.isBannerFullScreen ? '0vw' : '15vw',
            }} // set 15vw to column size and 0vw for full screen banner
            placeholderFontSize={{ base: '20px', md: '22px', lg: '24px' }} // Responsive font size
            placeholderFontWeight="bold"
            imageSrc={settings?.configs.bannerImageUrl}
            altText="Banner image"
            bannerText={
              categoryHeadings.find((item: Record<string, string>) => 'bannerText' in item)
                ?.bannerText
            }
            placeholderText={
              categoryHeadings.find(
                (item: Record<string, string>) => 'searchBarPlaceholderText' in item,
              )?.searchBarPlaceholderText || 'Search for Articles'
            }
            showOverlay={settings?.configs.showOverlay}
            showSearchBar={settings?.configs.showSearchBar}
            bannerTextColor={settings?.configs.bannerTextColor}
            bgColor={settings?.configs.secondaryBgColor}
            showJaggedBorder={settings?.configs.showJaggedBorder}
            secondaryAccentColor={settings?.configs.secondaryAccentColor}
          />
        )}

        {/* Main content with responsive padding */}
        <Box>
          {settings?.configs.showNotificationBanner && (
            <Box px={{ base: '2vw', md: '1vw', lg: '15vw' }}>
              <NotificationBanner
                notificationBannerBgColor={settings?.configs.notificationBannerBgColor ?? ''}
                notificationBannerTextColor={settings?.configs.notificationBannerTextColor ?? ''}
                notifications={settings?.data.notifications || []}
                notificationTranslations={notificationsData || []}
              />
            </Box>
          )}

          {/* Menu Cards */}
          {settings?.configs.showMenuCards && (
            <MenuCardList
              items={settings?.data.menuCards}
              itemsTranslations={menuList}
              menuCardsBgColor={settings?.configs.secondaryBgColor ?? ''}
              menuCradsTextColor={settings?.configs.secondaryTextColor ?? ''}
              onClickMenuCard={(id: string) => onClickMenuCard(id)}
            />
          )}
          {/* News Section */}

          {settings?.configs.showNewsSection && (
            <Box
              bgGradient={`linear(to-b, white, ${settings?.configs.tertiaryAccentColor})`}
              px={{ base: '1vw', md: '1vw', lg: '15vw' }}
            >
              <NewsSection
                newsArticles={newsArticles || []}
                newsArticlesTranslations={newsArticleList}
                newsArticleTextColor={settings?.configs.primaryTextColor}
                newsSectionHeadingTextColor={settings?.configs.secondaryTextColor}
                primaryAccentColor={settings?.configs.primaryAccentColor}
                secondaryAccentColor={settings?.configs.secondaryAccentColor}
                gradientAccentColor={settings.configs.gradientAccentColor}
                newsHeadlineTextColor={settings?.configs.newsHeadlineTextColor}
                newsSectionHeadingText={
                  categoryHeadings.find(
                    (item: Record<string, string>) => 'newsArticlesHeading' in item,
                  )?.newsArticlesHeading
                }
                newsSectionHelperTextView={
                  helperTexts.find((item: Record<string, string>) => 'viewAll' in item)?.viewAll
                }
                newsSectionHelperTextReadMore={
                  helperTexts.find((item: Record<string, string>) => 'readMore' in item)?.readMore
                }
              />
            </Box>
          )}
          <Box px={{ base: '2vw', md: '1vw', lg: '15vw' }}>
            {/* Popular Articles */}
            {settings?.configs.showPopularArticles && (
              <PopularArticles
                faqArticles={settings?.data.popularArticles}
                // faqArticleTranslations={popularArticleList}
                bgColor={settings?.configs.secondaryBgColor}
                faqArticlesTextColor={settings?.configs.primaryTextColor ?? ''}
                faqArticlesBgColor={settings?.configs.secondaryBgColor ?? ''}
                secondaryTextColor={settings?.configs.secondaryTextColor ?? ''}
                onClickPopularArticle={onClickPopularArticle}
                popularArticlesHeading={
                  categoryHeadings.find(
                    (item: Record<string, string>) => 'popularArticlesHeading' in item,
                  )?.popularArticlesHeading
                }
                faqsHeading={
                  categoryHeadings.find((item: Record<string, string>) => 'faqsHeading' in item)
                    ?.faqsHeading
                }
                primaryAccentColor={settings?.configs.primaryAccentColor}
                secondaryAccentColor={settings?.configs.secondaryAccentColor}
                tertiaryAccentColor={settings?.configs.tertiaryAccentColor}
                gradientAccentColor={settings.configs.gradientAccentColor}
                sectionIcons={settings.data.sectionIcons}
                faqBodyCharLimit={settings.configs.faqBodyCharLimit}
                popularArticleHref={popularArticleHref}
              />
            )}
          </Box>
          <Box>
            <Box px={{ base: '2vw', md: '1vw', lg: '15vw' }}>
              {/* section wise faq display */}
              {showSectionGrid && (
                <SectionGrid
                  textColor={settings?.configs.secondaryTextColor}
                  sectionBgColor={settings?.configs.secondaryBgColor}
                  sections={sectionsData}
                  sectionIconsTranslations={sectionIconsList}
                  sectionIcons={settings?.data.sectionIcons}
                  sectionsHeading={
                    categoryHeadings.find(
                      (item: Record<string, string>) => 'categoriesHeading' in item,
                    )?.categoriesHeading
                  }
                  sectionHelperText={
                    helperTexts.find((item: Record<string, string>) => 'more' in item)?.more
                  }
                  sectionCardTextColor={settings?.configs.primaryTextColor || 'black'}
                  primaryAccentColor={settings?.configs.primaryAccentColor}
                  secondaryAccentColor={settings?.configs.secondaryAccentColor}
                  gradientAccentColor={settings?.configs.gradientAccentColor}
                  categoriesHeadingTextColor={settings?.configs.categoriesHeadingTextColor}
                  onClickMore={onClickMore}
                  moreClickHref={moreClickHref}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Layout>
  );
};
